/**
 * Builds the green "candidate availability" calendar events for the
 * schedule-from-collected-availability flow (SCH-57).
 *
 * The candidate submits the days/times they're free; we render those
 * windows directly instead of computing interviewer availability. Each
 * window is split around the interviewer's foreground busy events so a
 * conflicting segment sits side-by-side with the busy block (the
 * recruiter consciously clicks a free slot) — the layout split is
 * driven entirely by CSS reacting to the class names emitted here
 * (`calendar/styles/events.css`, `.has-candidate-availability`).
 *
 * Pure port of `KInterviewScheduler/.../useCalendarEvents.tsx`
 * (`candidateAvailabilityEvents` memo). Kept free of React so it can be
 * unit-tested directly; `nowMs` is unused because candidate windows are
 * already past-clipped by the BE and FullCalendar greys past times.
 */
import moment from 'moment-timezone';
import type { EventInput } from '@fullcalendar/core';
import { EVENT_PRIORITY } from './eventStyles';
import type { Interval } from '../../availability';

/** Candidate-collected availability window — wire shape from
 *  `GET /ats_candidate_availabilities/:id/candidate_slots`. */
export interface CandidateAvailabilitySlot {
  start_at: string;
  end_at: string;
}

interface BuildCandidateAvailabilityEventsArgs {
  /** Raw candidate-collected windows. */
  rawSlots: ReadonlyArray<CandidateAvailabilitySlot>;
  /** Shown as the third line on each block (legacy parity). */
  candidateName?: string;
  /** Interviewer common availability for the visible week — drives the
   *  "mutual" (selectable, pulsing) vs "no-mutual" (dimmed) distinction.
   *  Empty = no interviewer picked yet → every segment is mutual. */
  commonAvailability: ReadonlyArray<Interval>;
  /** Interviewer/room foreground busy events for the visible week. Used
   *  both to split candidate windows and to mark the overlapping busy
   *  events (`overlappingBusyKeys`) so CSS can split the column. */
  busyEvents: ReadonlyArray<Interval>;
  /** Calendar timezone — all rendering math runs in this zone. */
  timezone: string;
  /** Selected interview duration (minutes). A candidate segment is
   *  "mutual" only when its overlap with interviewer availability is at
   *  least this long. */
  durationMinutes: number;
}

export interface CandidateAvailabilityEventsResult {
  events: EventInput[];
  /** `${start}_${end}` keys of busy events that overlap candidate
   *  availability — the caller appends `busy-event--has-candidate-overlap`
   *  to these so the CSS column-split engages. */
  overlappingBusyKeys: Set<string>;
}

const GREEN_BG = 'var(--kula-colors-pastel-2-light)';
const GREEN_TEXT = 'var(--kula-colors-pastel-2-dark)';

const fmt = (m: moment.Moment) => m.format('YYYY-MM-DDTHH:mm:ssZ');
const label = (s: moment.Moment, e: moment.Moment) =>
  `Available ${s.format('hh:mm A')} - ${e.format('hh:mm A')}`;

export function buildCandidateAvailabilityEvents({
  rawSlots,
  candidateName = '',
  commonAvailability,
  busyEvents,
  timezone,
  durationMinutes,
}: BuildCandidateAvailabilityEventsArgs): CandidateAvailabilityEventsResult {
  if (!rawSlots?.length) {
    return { events: [], overlappingBusyKeys: new Set<string>() };
  }

  // No interviewer working-hours data yet (none selected / still
  // loading) → treat every candidate window as mutual so the recruiter
  // can still pick from the candidate's stated availability.
  const hasInterviewerAvailabilityData = commonAvailability.length > 0;

  // Merge overlapping/adjacent candidate windows so we don't paint
  // duplicate segments where the candidate submitted touching slots.
  const sortedSlots = [...rawSlots]
    .map((s) => ({ start_at: s.start_at, end_at: s.end_at }))
    .sort(
      (a, b) => moment(a.start_at).valueOf() - moment(b.start_at).valueOf()
    );
  const slots: CandidateAvailabilitySlot[] = [];
  sortedSlots.forEach((s) => {
    const last = slots[slots.length - 1];
    if (last && moment(last.end_at).isSameOrAfter(moment(s.start_at))) {
      if (moment(s.end_at).isAfter(moment(last.end_at))) last.end_at = s.end_at;
    } else {
      slots.push({ start_at: s.start_at, end_at: s.end_at });
    }
  });

  const events: EventInput[] = [];
  const overlappingBusyKeys = new Set<string>();
  let eventId = 0;

  // A segment is "mutual" when it overlaps interviewer availability for
  // at least the interview duration — i.e. a slot that actually works
  // for both sides. Without interviewer data, everything is mutual.
  const isSegmentMutual = (segStart: moment.Moment, segEnd: moment.Moment) => {
    if (!hasInterviewerAvailabilityData) return true;
    return commonAvailability.some((avail) => {
      const overlapStart = moment.max(segStart, moment(avail.start));
      const overlapEnd = moment.min(segEnd, moment(avail.end));
      return (
        overlapEnd.isAfter(overlapStart) &&
        overlapEnd.diff(overlapStart, 'minutes') >= durationMinutes
      );
    });
  };

  const pushSegment = (
    start: moment.Moment,
    end: moment.Moment,
    slotIdx: number,
    opts: { isMutual: boolean; isDuringBusy: boolean }
  ) => {
    const { isMutual, isDuringBusy } = opts;
    // The during-busy segment must keep the plain `--no-mutual` class
    // (no `--no-busy-overlap`) so CSS pushes it to the right half beside
    // the busy block. Gap segments that aren't mutual get the
    // `--no-busy-overlap` marker so they stay full-width.
    const noBusyOverlapClass =
      !isMutual && !isDuringBusy
        ? ' candidate-availability-event--no-busy-overlap'
        : '';
    events.push({
      id: `candidate-avail-${slotIdx}-${eventId++}`,
      start: fmt(start),
      end: fmt(end),
      backgroundColor: GREEN_BG,
      textColor: GREEN_TEXT,
      editable: false,
      durationEditable: false,
      startEditable: false,
      classNames: `candidate-availability-event candidate-availability-event--${
        isMutual ? 'mutual' : 'no-mutual'
      }${noBusyOverlapClass}`,
      extendedProps: {
        type: 'candidate-availability',
        category: 'candidate-availability',
        priority: EVENT_PRIORITY.candidateAvailability,
        description: label(start, end),
        candidateName,
        isMutual,
      },
    });
  };

  slots.forEach((slot, slotIdx) => {
    const slotStart = moment(slot.start_at).tz(timezone);
    const slotEnd = moment(slot.end_at).tz(timezone);

    const overlappingBusies = busyEvents
      .filter(
        (busy) =>
          slotStart.isBefore(moment(busy.end)) &&
          slotEnd.isAfter(moment(busy.start))
      )
      .map((busy) => {
        // Key off the raw wire strings so the caller can match these
        // against the un-split external busy events. Render math runs in
        // the calendar tz so emitted segment strings stay consistent
        // with the candidate-window strings.
        overlappingBusyKeys.add(`${busy.start}_${busy.end}`);
        return {
          start: moment(busy.start).tz(timezone),
          end: moment(busy.end).tz(timezone),
        };
      })
      .sort((a, b) => a.start.valueOf() - b.start.valueOf());

    let currentStart = slotStart.clone();
    overlappingBusies.forEach((busy) => {
      const busyStart = moment.max(busy.start.clone(), slotStart.clone());
      const busyEnd = moment.min(busy.end.clone(), slotEnd.clone());
      if (busyEnd.isSameOrBefore(busyStart)) return;
      // Skip busy ranges already covered by an earlier (overlapping) busy.
      if (busyEnd.isSameOrBefore(currentStart)) return;
      // Gap before this busy block → full-width segment.
      if (currentStart.isBefore(busyStart)) {
        pushSegment(currentStart.clone(), busyStart.clone(), slotIdx, {
          isMutual: isSegmentMutual(currentStart, busyStart),
          isDuringBusy: false,
        });
      }
      // The portion of the candidate window that sits behind the busy
      // block → half-width, always no-mutual.
      const segmentStart = moment.max(currentStart.clone(), busyStart);
      pushSegment(segmentStart, busyEnd.clone(), slotIdx, {
        isMutual: false,
        isDuringBusy: true,
      });
      currentStart = busyEnd.clone();
    });

    // Tail after the last busy block (or the whole window when no busy).
    if (currentStart.isBefore(slotEnd)) {
      pushSegment(currentStart.clone(), slotEnd.clone(), slotIdx, {
        isMutual: isSegmentMutual(currentStart, slotEnd),
        isDuringBusy: false,
      });
    }
  });

  // Merge adjacent during-busy no-mutual segments (back-to-back busy
  // blocks would otherwise leave thin slivers). A segment is a
  // "during-busy" one when it's no-mutual AND lacks the no-busy-overlap
  // marker.
  const isBusyOverlapSegment = (evt: EventInput) =>
    !(evt.extendedProps as { isMutual?: boolean })?.isMutual &&
    !String(evt.classNames ?? '').includes(
      'candidate-availability-event--no-busy-overlap'
    );
  const merged: EventInput[] = [];
  for (const current of events) {
    if (isBusyOverlapSegment(current) && merged.length > 0) {
      const prev = merged[merged.length - 1];
      if (isBusyOverlapSegment(prev) && prev.end === current.start) {
        prev.end = current.end;
        (prev.extendedProps as { description?: string }).description = label(
          moment(prev.start as string),
          moment(current.end as string)
        );
        continue;
      }
    }
    merged.push(current);
  }

  return { events: merged, overlappingBusyKeys };
}
